import { getFirstAvailableConversationRoute } from '@shared/utils'
import { Sidebar } from 'primitives/sidebar'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { useSidebarTransition } from './app-sidebar.effects'
import { AppSidebarConversations } from './app-sidebar-conversations'
import { AppSidebarMain } from './app-sidebar-main'
import { AppSidebarReports, type ReportSection } from './app-sidebar-reports'
import { AppSidebarSettings } from './app-sidebar-settings'
import type { AvatarMenuProps } from './components/footer'

// Helper function to determine if sidebar should be collapsible based on current route
const shouldSidebarBeCollapsible = (pathname: string): boolean => {
  // Don't allow collapse for settings routes
  if (pathname.startsWith('/settings')) {
    return false
  }

  // Allow collapse for main navigation (including agent flow routes for auto-collapse)
  return true
}

// Helper function to render sidebar content based on type
const renderSidebarContent = (
  sidebarViewType: string,
  avatarProps: AvatarMenuProps | undefined,
  handleSettingsClick: () => void,
  handleOrganizationClick: (org: NonNullable<AvatarMenuProps['organizations']>[number]) => void,
  routes?: AppSidebarProps['routes'],
  viewsData?: AppSidebarProps['viewsData'],
  reportSections?: AppSidebarProps['reportSections'],
) => {
  // Base props shared by all sidebar components
  const baseProps = {
    user: avatarProps,
    onSettingsClick: handleSettingsClick,
    handleOrganizationClick,
  }

  switch (sidebarViewType) {
    case 'settings':
      return <AppSidebarSettings {...baseProps} routes={routes} />

    case 'conversations':
      return <AppSidebarConversations {...baseProps} viewsData={viewsData} />

    case 'reports':
      return <AppSidebarReports sections={reportSections} />

    case 'console-settings':
      return <AppSidebarSettings {...baseProps} routes={routes} />

    default:
      return null
  }
}

interface AppSidebarProps {
  avatarProps?: AvatarMenuProps
  setShowDuplicateSidebar?: (show: boolean) => void
  showDuplicateSidebar?: boolean
  routes?: Array<{
    title: string | React.ReactNode
    path: string
    icon?: React.ReactNode
    children?: Array<{
      title: string | React.ReactNode
      path: string
      icon?: React.ReactNode
      hidden?: boolean
      notAllowed?: boolean
    }>
    hidden?: boolean
    notAllowed?: boolean
    allowMultipleActiveAccordions?: boolean
  }>
  viewsData?: {
    favourites: Array<{ name: string; uuid: string }>
    publicViews: Array<{ name: string; uuid: string }>
    hasViews: boolean
    // Conversation counts and data
    activeConversations: number
    invitedContacts: number
    tasksData: number
    myTasksData: number
    unassignedTasks: number
    exceptionsTasks: number
    // User permissions
    isHumanAgent?: boolean
    hasLiveConversationPermission: boolean
    // Functions
    favouriteView: (params: { uuid: string; favourite: boolean }) => void
    authDetails: { name?: string } | null
  }
  handleSwitchOrganization: (payload: NonNullable<AvatarMenuProps['organizations']>[number]) => void
  onCreateOrganization?: () => void
  onLogoClick?: () => void
  reportSections?: ReportSection[]
  // Gates admin-only items in the billing sidebar (e.g. Limits) — same check
  // as the Account limits popover item / org-settings tab.
  isAdmin?: boolean
  // Extra app-provided item(s) rendered in the main sidebar's footer menu (e.g. promo banners).
  footerExtra?: React.ReactNode
}

interface AppSidebarInnerProps extends AppSidebarProps {
  sidebarViewType: string
  isCollapsible: boolean
  firstAvailableConversationRoute: string
  handleSettingsClick: () => void
  handleConversationsClick: () => void
  disableTransition?: boolean
  onCreateOrganization?: () => void
  onLogoClick?: () => void
}

const AppSidebarInner: React.FC<AppSidebarInnerProps> = ({
  avatarProps,
  viewsData,
  routes,
  handleSwitchOrganization,
  sidebarViewType,
  handleSettingsClick,
  handleConversationsClick,
  isCollapsible,
  disableTransition,
  onCreateOrganization,
  onLogoClick,
  reportSections,
  ...props
}) => {
  const handleOrganizationClick = (org: NonNullable<AvatarMenuProps['organizations']>[number]) => {
    handleSwitchOrganization(org)
  }

  if (sidebarViewType === 'main') {
    return (
      <AppSidebarMain
        {...props}
        avatarProps={avatarProps}
        viewsData={viewsData}
        routes={routes}
        onSettingsClick={handleSettingsClick}
        onConversationsClick={handleConversationsClick}
        isCollapsible={isCollapsible}
        handleOrganizationClick={handleOrganizationClick}
        disableTransition={disableTransition}
        onCreateOrganization={onCreateOrganization}
        onLogoClick={onLogoClick}
      />
    )
  }

  // Use helper function for other sidebar types
  return renderSidebarContent(
    sidebarViewType,
    avatarProps,
    handleSettingsClick,
    handleOrganizationClick,
    routes,
    viewsData,
    reportSections,
  )
}

export const AppSidebar = (props: AppSidebarProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  // Use centralized transition hook
  const { disableTransition, sidebarViewType, isSecondaryNav } = useSidebarTransition()

  const isCollapsible = shouldSidebarBeCollapsible(location.pathname)
  const firstAvailableConversationRoute = useMemo(
    () => getFirstAvailableConversationRoute(props.viewsData),
    [props.viewsData],
  )

  const handleSettingsClick = () => {
    navigate('/settings/profile')
  }

  const handleConversationsClick = () => {
    navigate(firstAvailableConversationRoute)
  }

  // Simple approach: Always render main sidebar, add secondary when needed
  if (isSecondaryNav) {
    return (
      <>
        {/* Primary sidebar - collapsed */}
        <AppSidebarMain
          {...props}
          avatarProps={props.avatarProps}
          viewsData={props.viewsData}
          routes={props.routes}
          onSettingsClick={handleSettingsClick}
          onConversationsClick={handleConversationsClick}
          isCollapsible={true}
          handleOrganizationClick={props.handleSwitchOrganization}
          onCreateOrganization={props.onCreateOrganization}
          onLogoClick={props.onLogoClick}
          disableTransition={disableTransition}
        />

        {/* Secondary sidebar - render content directly to avoid nested Sidebar components.
            Hidden below md: as a fixed w-56 column it crushes content on mobile (billing is
            the only secondary-nav section reachable on mobile). */}
        <Sidebar
          side="right"
          collapsible="none"
          className="hidden md:flex w-56 !right-auto !left-[var(--sidebar-width-icon)] border-r h-svh flex-col"
          style={{ '--sidebar-width': '15rem' } as React.CSSProperties}
        >
          {renderSidebarContent(
            sidebarViewType,
            props.avatarProps,
            handleSettingsClick,
            props.handleSwitchOrganization,
            props.routes,
            props.viewsData,
            props.reportSections,
          )}
        </Sidebar>
      </>
    )
  }

  // Main nav only
  return (
    <AppSidebarInner
      {...props}
      sidebarViewType={sidebarViewType}
      isCollapsible={isCollapsible}
      firstAvailableConversationRoute={firstAvailableConversationRoute}
      handleSettingsClick={handleSettingsClick}
      handleConversationsClick={handleConversationsClick}
      disableTransition={disableTransition}
    />
  )
}
